import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { unwrapList } from '../../utils/format.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import { createAmbulanceRequest } from '../../api/ambulance.js'
import { useAuth } from '../../auth/AuthProvider.jsx'

/**
 * Patient-facing ambulance directory.
 *
 * Lists every active ambulance service in the network with logo,
 * coverage area, operating hours, and a tel: dial button. Unlike lab
 * and imaging this isn't a stock catalogue — ambulance services
 * dispatch on-demand rather than fulfilling itemised orders, so the
 * surface is a directory + call-to-action.
 *
 * In-app dispatch (patient → ambulance crew) flows through clinical
 * notes today (doctor places an AmbulanceRequest from the CNE). A
 * patient-initiated dispatch flow is on the roadmap; for now a tel:
 * link puts them in contact with dispatch directly, which matches the
 * PNG context where many emergency calls happen by phone anyway.
 */
export default function PatientAmbulanceBrowser() {
  const [q, setQ] = useState('')
  const debounced = useDebounce(q, 250)

  const servicesQ = useQuery({
    queryKey: ['ambulance-services', debounced],
    queryFn: async () => {
      const params = { is_active: true, ordering: 'name' }
      if (debounced.trim().length >= 2) params.search = debounced.trim()
      const { data } = await api.get(endpoints.ambulanceServiceList, { params })
      return unwrapList(data)
    },
    staleTime: 60_000,
  })
  const rows = servicesQ.data || []

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-3">
          <div className="text-2xl">🚑</div>
          <div className="flex-1">
            <CardHeader
              title="Ambulance services"
              subtitle="Dispatch services covering Papua New Guinea. Tap a service to call dispatch directly. In a life-threatening emergency, call your nearest service's number now."
              right={
                <Link to="/patient" className="text-xs text-wantok-700 underline">
                  ← Back to my home
                </Link>
              }
            />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Search by service name, town, or coverage area…'
              className="w-full border rounded px-3 py-2 text-sm mt-2"
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
          <b>Life-threatening emergency?</b> Call dispatch directly using
          the phone numbers below. The Wantok app is not a substitute for
          calling an ambulance in a real emergency.
        </div>
      </Card>

      {servicesQ.error && <ErrorBanner error={servicesQ.error} onRetry={() => servicesQ.refetch()} />}

      {servicesQ.isLoading && (
        <div className="text-sm text-slate-400 text-center py-6">Loading services…</div>
      )}

      {!servicesQ.isLoading && rows.length === 0 && (
        <Card>
          <div className="text-sm text-slate-500">
            No ambulance services match your search. Clear the filter to
            see every active service.
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((svc) => (
          <ServiceCard key={svc.id} service={svc} />
        ))}
      </div>
    </div>
  )
}

function ServiceCard({ service }) {
  const tel = (service.phone_number || '').replace(/[^+\d]/g, '')
  const [requestOpen, setRequestOpen] = useState(false)
  return (
    <Card>
      <div className="flex items-start gap-3">
        <CatalogueImageThumb
          url={service.logo_url}
          alt={service.name}
          size={56}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">
            {service.name}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
            {service.coverage_area && (
              <div><span className="uppercase tracking-wide text-slate-400 text-[10px] mr-1">Coverage:</span>{service.coverage_area}</div>
            )}
            {service.operating_hours && (
              <div><span className="uppercase tracking-wide text-slate-400 text-[10px] mr-1">Hours:</span>{service.operating_hours}</div>
            )}
            {service.address && (
              <div className="truncate"><span className="uppercase tracking-wide text-slate-400 text-[10px] mr-1">Base:</span>{service.address}</div>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {tel ? (
            <a
              href={`tel:${tel}`}
              className="text-sm bg-rose-600 hover:bg-rose-700 text-white rounded px-3 py-1.5 no-underline whitespace-nowrap"
              title="Call dispatch — for emergencies, calling is fastest"
            >
              ☎ Call dispatch
            </a>
          ) : (
            <span className="text-[11px] text-slate-400 italic">No phone on file</span>
          )}
          <button
            onClick={() => setRequestOpen(true)}
            className="text-xs border border-wantok-300 text-wantok-700 hover:bg-wantok-50 rounded px-3 py-1 whitespace-nowrap"
            title="Request non-emergency transport in the app — the dispatcher will follow up"
          >
            Request transport
          </button>
          {service.phone_number && (
            <span className="text-[11px] text-slate-500 font-mono">{service.phone_number}</span>
          )}
          {service.email && (
            <a href={`mailto:${service.email}`} className="text-[11px] text-wantok-700 underline">
              {service.email}
            </a>
          )}
        </div>
      </div>
      {requestOpen && (
        <RequestTransportModal
          service={service}
          onClose={() => setRequestOpen(false)}
        />
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// In-app, patient-initiated transport request. Backend allows this since
// migration 0022 (AmbulanceRequest.encounter is nullable; a direct
// patient FK is auto-filled from request.user). Use cases: scheduled
// hospital pickup, follow-up transfer, dialysis run — anything where
// the patient knows in advance and wants the request tracked in-app
// rather than over the phone.
//
// Emergency framing: the modal clearly suggests "Call dispatch" for
// emergencies and only enables in-app submit for routine/priority. We
// could let patients submit emergencies too, but mixed signals about
// severity at intake risk a real one being deprioritised, so we route
// emergencies to the phone where dispatch can take a real-time triage.
// ---------------------------------------------------------------------------
function RequestTransportModal({ service, onClose }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [serviceType, setServiceType] = useState('Routine transport')
  const [pickup, setPickup] = useState(user?.profile?.address || '')
  const [destination, setDestination] = useState('')
  const [urgency, setUrgency] = useState('routine')
  const [summary, setSummary] = useState('')

  const submit = useMutation({
    mutationFn: () => createAmbulanceRequest({
      ambulance_service: service.id,
      service_type: serviceType.trim(),
      pickup_location: pickup.trim(),
      destination: destination.trim(),
      urgency,
      clinical_summary: summary.trim(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-amb'] })
      onClose()
    },
  })

  const tel = (service.phone_number || '').replace(/[^+\d]/g, '')
  const isEmergency = urgency === 'emergency'
  const canSubmit = (
    serviceType.trim().length > 0 &&
    pickup.trim().length > 0 &&
    !isEmergency &&
    !submit.isPending
  )

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Request transport</div>
            <div className="text-xs text-slate-500 truncate">{service.name}</div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-lg leading-none"
            disabled={submit.isPending}
          >×</button>
        </div>

        <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
          <b>Life-threatening emergency?</b> Tap the red <span className="font-mono">☎ Call dispatch</span> button on
          the service card — calling is faster and the crew can triage
          in real time. Use this in-app form only for routine or planned
          (non-emergency) transport.
        </div>

        <div className="grid gap-2">
          <Field label="Type of transport">
            <input
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              placeholder="e.g. Routine transport, Inter-facility transfer, Dialysis run"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Pickup address" required>
            <input
              value={pickup}
              onChange={(e) => setPickup(e.target.value)}
              placeholder="Where the crew should pick you up"
              className="w-full border rounded px-2 py-1.5 text-sm"
              required
            />
          </Field>
          <Field label="Destination">
            <input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Hospital or address"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Urgency">
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="routine">Routine — planned transport</option>
              <option value="priority">Priority — needs same-day attention</option>
              <option value="emergency">Emergency — call dispatch instead</option>
            </select>
            {isEmergency && (
              <div className="mt-1 text-[11px] text-rose-700">
                For an emergency please call dispatch directly{tel ? ` on ${service.phone_number}` : ''}.
                The in-app form is disabled when urgency is set to Emergency.
              </div>
            )}
          </Field>
          <Field label="Clinical summary / notes">
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Any details the crew should know — relevant conditions, mobility, accompanying carer, etc."
              rows={3}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </Field>
        </div>

        {submit.error && <ErrorBanner error={submit.error} />}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            disabled={submit.isPending}
            className="text-xs text-slate-600 hover:underline disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={() => submit.mutate()}
            disabled={!canSubmit}
            className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-4 py-1.5 disabled:opacity-40"
          >
            {submit.isPending ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">
      {label}{required && <span className="text-rose-600">*</span>}
      <div className="mt-1 normal-case font-normal">{children}</div>
    </label>
  )
}
